/**
 * Product naming.
 *
 * The final product name is not decided. `monrdv` (from *Mon Rendez-vous*, the working
 * domain `monrdv.ca`) is used as the **internal codename**: the npm scope, the database
 * role, the Postgres text-search configuration, the Redis channel prefix, the systemd unit
 * names.
 *
 * Those internal identifiers are deliberately separated from the **display name** below.
 * Renaming a database role or an npm scope across a live deployment is disruptive and
 * gains nothing, whereas the name people read should be changeable in one commit. So:
 *
 *   - Marketing picks a new name  → change `PRODUCT_NAME` here. That is the whole job.
 *   - The codename `monrdv` stays → no migration, no scope rename, no redeploy dance.
 *
 * In practice the display name is barely visible anyway: each tenant is white-labelled and
 * the interface shows *their* business name and colour. `PRODUCT_NAME` appears on the
 * sign-in screen, in the PWA manifest, in outbound email footers and in the document title.
 */

/** Shown to users. Change this one constant when the real name is chosen. */
export const PRODUCT_NAME = 'Mon Rendez-vous';

/** Short form, for the PWA home-screen label where space is tight (12 chars or so). */
export const PRODUCT_NAME_SHORT = 'MonRDV';

/** Stable internal codename. Do not change; it is baked into infrastructure identifiers. */
export const CODENAME = 'monrdv';

/**
 * Primary domain, used to build absolute links in email and SMS.
 *
 * This package is imported by the API (Node) and by both browser apps, so it cannot assume
 * either environment's globals exist — reading `process.env` directly crashes the browser
 * build. The guarded lookup keeps one shared module usable from all three.
 */
export const PRIMARY_DOMAIN: string = readEnv('PUBLIC_DOMAIN') ?? 'monrdv.ca';

function readEnv(name: string): string | undefined {
  const nodeEnv = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  if (nodeEnv?.[name]) return nodeEnv[name];
  try {
    const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    return viteEnv?.[`VITE_${name}`] ?? viteEnv?.[name];
  } catch {
    return undefined;
  }
}

/** The technician app's identity, distinct from the portal's in the PWA manifest. */
export const FIELD_APP_NAME = { fr: 'Mon Rendez-vous — Terrain', en: 'Mon Rendez-vous — Field' } as const;
export const PORTAL_APP_NAME = { fr: 'Mon Rendez-vous — Bureau', en: 'Mon Rendez-vous — Office' } as const;
